import { type EditingRange, type ITextContent, type ITextLayerProps } from '@/version-types';

type RichTextProcessPayload<K extends keyof ITextContent = keyof ITextContent> = {
    key: K;
    value: ITextContent[K];
};

export class RichTextProcessor {
    private constructor() {
        throw new Error('RichTextProcessor is a static class');
    }

    private static initTextContents(layerData: ITextLayerProps) {
        const { textContents = [] } = layerData;
        if (textContents.length > 0) return textContents;

        const item: ITextContent = {
            text: layerData.text,
            color: layerData.color,
            fontSize: layerData.fontSize,
            fontFamily: layerData.fontFamily,
            bold: layerData.bold,
            italic: layerData.italic,
            lineThrough: layerData.lineThrough,
            underline: layerData.underline,
            textMark: layerData.textMark,
        };

        return [item];
    }

    private static processWithinItem(
        textContents: ITextContent[],
        range: EditingRange,
        payload: RichTextProcessPayload,
    ) {
        const idx = range.startIndex; // startIndex === endIndex
        const item = textContents[idx];

        if (!item) {
            console.warn('[RichTextProcessor.processWithinItem]: idx is out of range, please check');
            return { textContents, range };
        }

        const { startOffset, endOffset } = range;
        const text = item.text;

        const segments = (() => {
            if (endOffset >= text.length) {
                if (startOffset === 0) return [[0, text.length]];
                return [
                    [0, startOffset],
                    [startOffset, text.length],
                ];
            }

            if (startOffset === 0) {
                return [
                    [0, endOffset],
                    [endOffset, text.length],
                ];
            }

            return [
                [0, startOffset],
                [startOffset, endOffset],
                [endOffset, text.length],
            ];
        })();

        const { key, value } = payload;

        const newTextContents = segments.map((seg, i) => {
            const [start, end] = seg;
            const segText = text.substring(start, end);
            const ret: ITextContent = { ...item, text: segText };

            // 设置分段属性条件说明：
            // 1. 当拆分结果为 1 段时，即设置的还是当前整段属性时，则 i === 0 时进行设置
            // 2. 当拆分结果为 2 段时，需要通过 startOffset 进行判断，若 startOffset === 0，则设置前半段属性（即 i === 0），否则设置后半段属性（即 i === 1）
            // 3. 当拆分结果为 3 段时，设置中间段属性，即 i === 1 时进行设置

            if (
                (segments.length === 1 && i === 0) ||
                (segments.length === 2 && startOffset === 0 && i === 0) ||
                (startOffset !== 0 && i === 1) ||
                (segments.length === 3 && i === 1)
            ) {
                Reflect.set(ret, key, value);
            }

            return ret;
        });

        const before = textContents.slice(0, idx);
        const after = textContents.slice(idx + 1);
        const nextTextContents = before.concat(newTextContents).concat(after);
        const nextRange = (() => {
            const ret: EditingRange = { ...range };
            if (segments.length === 2) {
                if (range.startOffset === 0) {
                    ret.endOffset = newTextContents[0].text.length;
                } else {
                    ret.startIndex++;
                    ret.endIndex++;
                    ret.startOffset = 0;
                    ret.endOffset = newTextContents[1].text.length;
                }

                ret.isEnd = idx === textContents.length - 1 && range.endOffset === text.length;
            }

            if (segments.length === 3) {
                ret.startIndex++;
                ret.endIndex++;
                ret.startOffset = 0;
                ret.endOffset = newTextContents[1].text.length;
                ret.isEnd = false;
            }

            return ret;
        })();

        return { textContents: nextTextContents, range: nextRange };
    }

    private static processAcrossItems(
        textContents: ITextContent[],
        range: EditingRange,
        payload: RichTextProcessPayload,
    ) {
        const { startIndex, endIndex, startOffset, endOffset } = range;
        const startItem = textContents[startIndex];
        const endItem = textContents[endIndex];

        if (!startItem || !endItem) {
            console.warn('[RichTextProcessor.processAcrossItems]: idx is out of range, please check');
            return { textContents, range };
        }

        const { key, value } = payload;

        // handle startItem
        let splitInStart: ITextContent[] = [];
        if (startOffset === 0) {
            const item = { ...startItem, [key]: value };
            splitInStart = [item];
        } else {
            const segments = [
                [0, startOffset],
                [startOffset, startItem.text.length],
            ];
            splitInStart = segments.map((seg, i) => {
                const [start, end] = seg;
                const segText = startItem.text.substring(start, end);
                const ret: ITextContent = { ...startItem, text: segText };

                if (i === 1) Reflect.set(ret, key, value);

                return ret;
            });
        }

        // handle endItem
        let splitInEnd: ITextContent[] = [];
        if (endOffset === endItem.text.length) {
            const item = { ...endItem, [key]: value };
            splitInEnd = [item];
        } else {
            const segments = [
                [0, endOffset],
                [endOffset, endItem.text.length],
            ];
            splitInEnd = segments.map((seg, i) => {
                const [start, end] = seg;
                const segText = endItem.text.substring(start, end);
                const ret: ITextContent = { ...endItem, text: segText };

                if (i === 0) Reflect.set(ret, key, value);

                return ret;
            });
        }

        // combine
        const before = textContents.slice(0, startIndex);
        const after = textContents.slice(endIndex + 1);
        let middle = textContents.slice(startIndex + 1, endIndex);
        if (middle.length > 0) {
            middle = middle.map((item) => ({ ...item, [key]: value }));
        }

        const nextTextContents = before.concat(splitInStart).concat(middle).concat(splitInEnd).concat(after);

        // update range
        const nextRange = (() => {
            const ret: EditingRange = { ...range };

            if (splitInStart.length > 1) {
                ret.startIndex++;
                ret.endIndex++;
                ret.startOffset = 0;
            }

            return ret;
        })();

        return { textContents: nextTextContents, range: nextRange };
    }

    static process(layerData: ITextLayerProps, range: EditingRange, payload: RichTextProcessPayload) {
        const textContents = this.initTextContents(layerData);
        const { startIndex, endIndex } = range;

        if (startIndex === endIndex) {
            return this.processWithinItem(textContents, range, payload);
        }

        return this.processAcrossItems(textContents, range, payload);
    }
}
